import clsx from 'clsx';
import { ReactNode, RefObject, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import BaseCalloutOrToast from './base';
import styles from './Callout.module.css';
import { Placement, useCalloutPosition } from './useCalloutPosition';

const ANIMATION_MS = 180; // must match the transition duration in Callout.module.css

interface CalloutProps {
  anchorRef: RefObject<HTMLElement>;
  open: boolean;
  close: () => void;
  content: ReactNode;
  callToAction?: ReactNode;
  color?: 'grey' | 'teal' | 'orange';
  icon?: 'info' | 'warning' | 'tick';
  placement?: Placement;
  closeOnOutsideClick?: boolean;
  label?: string;
}

const Callout = ({
  anchorRef,
  open,
  close,
  content,
  callToAction,
  color = 'grey',
  icon = 'info',
  placement = 'bottom',
  closeOnOutsideClick = true,
  label = 'callout dialog',
}: CalloutProps) => {
  const calloutRef = useRef<HTMLDivElement>(null);
  const openedAt = useRef(0);

  // Stays true for the duration of the exit animation.
  const [mounted, setMounted] = useState(open);
  // Drives the enter/exit class; flipped one frame after we have a position.
  const [visible, setVisible] = useState(false);

  const position = useCalloutPosition({
    anchorRef,
    calloutRef,
    open: mounted, // measure while mounted, not just while open
    preferred: placement,
  });

  // Mount immediately on open; delay unmount until the exit transition ends.
  useEffect(() => {
    if (open) {
      setMounted(true);
      return;
    }
    setVisible(false);
    const timeout = setTimeout(() => setMounted(false), ANIMATION_MS);
    return () => clearTimeout(timeout);
  }, [open]);

  // Only animate in once a position exists, so it doesn't slide in from 0,0.
  useEffect(() => {
    if (!mounted || !position) return;
    const raf = requestAnimationFrame(() => setVisible(true));
    return () => cancelAnimationFrame(raf);
  }, [mounted, position]);

  // Outside click + Escape to close.
  useEffect(() => {
    if (!open) return;

    openedAt.current = Date.now();

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
    };

    const onPointerDown = (e: PointerEvent) => {
      if (!closeOnOutsideClick) return;
      if (Date.now() - openedAt.current < 150) return; // ignore the opening interaction
      const target = e.target as Node;
      if (calloutRef.current?.contains(target)) return;
      if (anchorRef.current?.contains(target)) return;
      close();
    };

    document.addEventListener('keydown', onKeyDown);
    document.addEventListener('pointerdown', onPointerDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('pointerdown', onPointerDown);
    };
  }, [open, close, closeOnOutsideClick, anchorRef]);

  if (!mounted || typeof document === 'undefined') return null;

  return createPortal(
    <div
      ref={calloutRef}
      role="dialog"
      aria-label={label}
      aria-modal="false"
      className={clsx(
        styles.callout,
        position && styles.positioned,
        visible ? styles.enter : styles.exit,
        position && styles[`origin__${position.placement}`],
      )}
      style={{ top: position?.top ?? 0, left: position?.left ?? 0 }}
    >
      <BaseCalloutOrToast
        close={() => close()}
        content={content}
        callToAction={callToAction}
        color={color}
        icon={icon}
        type="callout"
      />
      {position && (
        <span
          aria-hidden
          className={clsx(
            styles.arrow,
            styles[`arrow__${position.placement}`],
            styles[`arrowColor__${color}`],
          )}
          style={{ left: position.arrowLeft }}
        />
      )}
    </div>,
    document.body,
  );
};

export default Callout;